import { CONSOLES, entitledConsoles } from "./consoles";

// Which dashboard this browser prefers, persisted across sessions.
//
// Mirrors the storage contract already used by src/v2/ops/pages/orders-legacy/
// columnPrefs.js and src/v2/pharmacy/pages/orders/sortPresets.js: a versioned JSON
// envelope, every read and write wrapped so it can never throw, and a silent no-op when
// storage is unavailable (private mode, disabled site data). A UI preference is not
// worth failing a render -- or a LOGIN -- over, and this one is read during the login
// redirect.
//
// Per-browser, not per-user: there is no backend preference store today
// (userDetails.additionalDetails exists but has no generic write endpoint), so this
// deliberately does not follow a user across devices.

const STORAGE_KEY = "pillup.app.console.preferred";
const STORAGE_VERSION = 1;

/**
 * The stored console id, or null to mean "no usable preference -- use the role default".
 *
 * Returns null when storage is unavailable, the value is malformed, the version is from
 * an older shape, the id is not a real console, or the user is no longer entitled to it
 * (their roles changed).
 *
 * Note it does NOT clear storage on a failed entitlement check. A role that is
 * momentarily absent -- a stale cookie, a mid-flight role change -- must not silently
 * erase a deliberate choice; the stored value simply goes dormant and re-activates if
 * entitlement returns.
 */
export function loadPreferredConsoleId(userRoles = []) {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.version !== STORAGE_VERSION) return null;
        if (!CONSOLES.some((entry) => entry.id === parsed.consoleId)) return null;
        if (!entitledConsoles(userRoles).some((entry) => entry.id === parsed.consoleId)) return null;
        return parsed.consoleId;
    } catch (error) {
        return null;
    }
}

/** Persist the chosen console id. Silently no-ops if storage is unavailable. */
export function savePreferredConsoleId(consoleId) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, consoleId }));
    } catch (error) {
        // Ignored on purpose -- the choice still applies for this session.
    }
}
